import { create } from "zustand";
import { ShortLGS } from "modules/common/model/models";

/**
 * Zustand store for LGS (Local Game Store) settings
 * Manages settings dialog state and selected stores globally across the application
 *
 * Selected stores are used in:
 * - Onboarding flow for initial store selection
 * - Map component for highlighting user's selected stores
 * - Store selection forms for bidirectional sync between map and grid
 *
 * Note: colsState and itemLimitState atoms were removed as they were unused in the codebase
 */
interface LGSSettingsStore {
  // Dialog state
  isOpen: boolean;

  // Coordinates to center the dialog map on when it opens (consumed once)
  pendingCoords: [number, number] | null; // [lng, lat]

  // Selected stores state (global state used across onboarding, maps, and store selection forms)
  selectedStores: ShortLGS[];

  // Actions
  setOpen: (open: boolean) => void;
  toggle: () => void;
  openWithCoords: (coords: [number, number]) => void;
  clearPendingCoords: () => void;
  setSelectedStores: (stores: ShortLGS[]) => void;
  addStore: (store: ShortLGS) => void;
  removeStore: (wotcId: number) => void;
  toggleStoreSelection: (store: ShortLGS) => void;
  isStoreSelected: (wotcId: number) => boolean;
}

export const useLGSSettingsStore = create<LGSSettingsStore>((set, get) => ({
  // Initial state
  isOpen: false,
  pendingCoords: null,
  selectedStores: [],

  // Actions
  setOpen: (open) => set({ isOpen: open }),
  toggle: () => set({ isOpen: !get().isOpen }),
  openWithCoords: (coords) => set({ pendingCoords: coords, isOpen: true }),
  clearPendingCoords: () => set({ pendingCoords: null }),

  setSelectedStores: (stores) => set({ selectedStores: stores }),

  addStore: (store) =>
    set((state) => {
      // Avoid duplicates by checking wotcId
      const exists = state.selectedStores.some(
        (s) => s.wotcId === store.wotcId
      );
      if (exists || state.selectedStores.length >= 5) return state;
      return { selectedStores: [...state.selectedStores, store] };
    }),

  removeStore: (wotcId) =>
    set((state) => ({
      selectedStores: state.selectedStores.filter((s) => s.wotcId !== wotcId),
    })),

  toggleStoreSelection: (store) => {
    const state = get();
    const exists = state.selectedStores.some((s) => s.wotcId === store.wotcId);
    if (exists) {
      state.removeStore(store.wotcId);
    } else {
      state.addStore(store);
    }
  },

  isStoreSelected: (wotcId) =>
    get().selectedStores.some((s) => s.wotcId === wotcId),
}));

/**
 * Semantic selector hook for LGS settings dialog state
 * Returns: isOpen, setOpen, toggle
 */
export const useLGSSettingsDialog = () => {
  const isOpen = useLGSSettingsStore((state) => state.isOpen);
  const setOpen = useLGSSettingsStore((state) => state.setOpen);
  const toggle = useLGSSettingsStore((state) => state.toggle);
  const openWithCoords = useLGSSettingsStore((state) => state.openWithCoords);
  return { isOpen, setOpen, toggle, openWithCoords };
};

/**
 * Semantic selector hook for pending coordinates state
 * Used by SelectLGSDialog to sync location when it opens
 */
export const useLGSSettingsPendingCoords = () => {
  const pendingCoords = useLGSSettingsStore((state) => state.pendingCoords);
  const clearPendingCoords = useLGSSettingsStore(
    (state) => state.clearPendingCoords
  );
  return { pendingCoords, clearPendingCoords };
};

/**
 * Semantic selector hook for selected stores state
 * Returns: selectedStores, setSelectedStores, addStore, removeStore, toggleStoreSelection, isStoreSelected
 */
export const useSelectedStores = () => {
  const selectedStores = useLGSSettingsStore((state) => state.selectedStores);
  const setSelectedStores = useLGSSettingsStore(
    (state) => state.setSelectedStores
  );
  const addStore = useLGSSettingsStore((state) => state.addStore);
  const removeStore = useLGSSettingsStore((state) => state.removeStore);
  const toggleStoreSelection = useLGSSettingsStore(
    (state) => state.toggleStoreSelection
  );
  const isStoreSelected = useLGSSettingsStore((state) => state.isStoreSelected);

  return {
    selectedStores,
    setSelectedStores,
    addStore,
    removeStore,
    toggleStoreSelection,
    isStoreSelected,
  };
};
